import type { Dispatch, SetStateAction } from 'react';
import { Fragment, useContext, useRef, useState } from 'react';

import {
  getActionAriaLabel,
  getLinkAriaLabel,
  getUrlText,
} from '@/utils/urlUtils';

import { AspectRatioImage } from '@/components/AspectRatioImage/AspectRatioImage';
import { useCompoundContext } from '@/components/Header/context';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { navToFindStores } from '@/components/Megamenu/helpers';
import { useMobileNavMainMenuFocus } from '@/components/Megamenu/hooks/useMobileNavMainMenuFocus';
import { Typography } from '@/components/Typography';
import type {
  ContentfulMainHeaderFragment,
  ContentfulMainMenuItemFragment,
} from '@/graphql/contentful/api';
import { useHeaderOffSet } from '@/hooks/useHeaderOffSet';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import { MobileMenuAccordion } from '../MobileMenuAccordion';
import { Scroller } from '../Scroller';

import * as S from './MobileNav.styles';

export interface MobileNavProps {
  isOpenSubDrawer: boolean;
  items: ContentfulMainHeaderFragment;
  onClose: (isOpen: boolean) => void;
  setIsOpenSubDrawer: Dispatch<SetStateAction<boolean>>;
}

export const MobileNav = ({
  isOpenSubDrawer,
  items,
  onClose,
  setIsOpenSubDrawer,
}: MobileNavProps) => {
  useCompoundContext();
  const { locale: regionLocale } = useRegionProvider();
  const [isOpenSubmenu, setIsOpenSubmenu] = useState<{
    [key: string]: boolean;
  }>({});
  const top = useHeaderOffSet();
  const mainMenuSectionRef = useRef<HTMLDivElement>(null);

  useMobileNavMainMenuFocus(mainMenuSectionRef, isOpenSubDrawer);

  const toggleSubmenu = (key: string) => {
    setIsOpenSubmenu((prev) => {
      const newState: { [key: string]: boolean } = {};

      Object.keys(prev).forEach((key) => {
        newState[key] = false;
      });

      newState[key] = true;

      return newState;
    });
  };

  const closeSubDrawer = () => {
    setIsOpenSubDrawer(false);
    setIsOpenSubmenu({});
  };

  const openSubmenu = (item: ContentfulMainMenuItemFragment | null) => {
    if (!item) {
      return;
    }
    toggleSubmenu(item.internalName as string);
  };

  function makeSubmenu(item: ContentfulMainMenuItemFragment | null) {
    if (!item) {
      return null;
    }

    const submenuContent = item?.submenuItemsCollection?.items.map(
      (subItem, index) => {
        const isItemWithChildren = subItem?.submenuItemsCollection?.items || [];

        if (item?.type === 'Collection') {
          return (
            <CollectionItem
              key={subItem?.internalName}
              idx={index}
              item={subItem as ContentfulMainMenuItemFragment}
              onClose={onClose}
            />
          );
        }

        if (
          subItem?.visibleInMobile &&
          (isItemWithChildren?.length as number) > 0
        ) {
          return (
            <ItemWithChildren
              key={subItem?.internalName}
              data-test-id={`MobileNavItemWithChildren-${subItem?.internalName}`}
              item={subItem as ContentfulMainMenuItemFragment}
              onClose={onClose}
            />
          );
        }

        return null;
      },
    );

    if (item?.type === 'Collection') {
      return (
        <S.CollectionBox>
          <S.MobileCollectionBox data-test-id='MobileNavCollectionBox'>
            {submenuContent}
          </S.MobileCollectionBox>
          {!!item?.bottomLinkLabel && (
            <S.LinkBox data-test-id='MobileCollectionsLinkBox'>
              <LinkWithLocale
                aria-label={
                  getLinkAriaLabel(
                    item?.bottomLinkUrl ?? '',
                    regionLocale.code,
                  ) ||
                  item?.bottomLinkAriaLabel ||
                  item?.bottomLinkLabel ||
                  undefined
                }
                href={item?.bottomLinkUrl ?? ''}
                onClick={() => onClose(false)}
              >
                {item?.bottomLinkLabel}
              </LinkWithLocale>
            </S.LinkBox>
          )}
        </S.CollectionBox>
      );
    }

    return submenuContent;
  }

  return (
    <S.Wrapper data-test-id='MobileNavWrapper'>
      {items?.megamenu?.menuItemsCollection?.items.length ? (
        <S.MainMenuSection
          ref={mainMenuSectionRef}
          aria-hidden={isOpenSubDrawer}
          data-test-id='MobileNavMainMenuSection'
        >
          <S.List>
            {items?.megamenu?.menuItemsCollection?.items.map((item, index) => {
              const isItemWithChildren = item?.submenuItemsCollection?.items;

              return (
                item?.visibleInMobile &&
                ((isItemWithChildren?.length as number) > 0 ? (
                  <S.ParentItem key={item?.internalName}>
                    <S.ItemContentRow
                      data-test-id={`MobileNavParentItem-${index}`}
                      data-testid={`MobileNavParentItem-${index}`}
                    >
                      {item?.type === 'Title' ? (
                        // Title items are group headers on mobile: tapping the
                        // label expands the submenu instead of navigating.
                        <S.TitleLabelButton
                          aria-controls='mobile-submenu-panel'
                          aria-expanded={
                            isOpenSubDrawer &&
                            Boolean(isOpenSubmenu[item?.internalName ?? ''])
                          }
                          aria-label={`${item?.label ?? ''}, expand submenu`}
                          onClick={() => {
                            setIsOpenSubDrawer(true);
                            openSubmenu(item as ContentfulMainMenuItemFragment);
                          }}
                          type='button'
                        >
                          <Typography scale='h4'>{item?.label}</Typography>
                        </S.TitleLabelButton>
                      ) : (
                        <S.ParentNavLink
                          aria-label={getLinkAriaLabel(
                            item.url ?? '',
                            regionLocale.code,
                          )}
                          href={item.url ?? ''}
                          onClick={() => onClose(false)}
                        >
                          <Typography scale='h4'>{item?.label}</Typography>
                        </S.ParentNavLink>
                      )}
                      <S.ExpandSubmenuButton
                        aria-controls='mobile-submenu-panel'
                        aria-expanded={
                          isOpenSubDrawer &&
                          Boolean(isOpenSubmenu[item?.internalName ?? ''])
                        }
                        aria-label={`${item?.label ?? ''}, expand submenu`}
                        onClick={() => {
                          setIsOpenSubDrawer(true);
                          openSubmenu(item as ContentfulMainMenuItemFragment);
                        }}
                        type='button'
                      >
                        <S.ArrowRightIcon aria-hidden focusable={false} />
                      </S.ExpandSubmenuButton>
                    </S.ItemContentRow>
                  </S.ParentItem>
                ) : (
                  <LinkItem
                    key={item?.internalName}
                    item={item as ContentfulMainMenuItemFragment}
                    onClose={onClose}
                  />
                ))
              );
            })}
          </S.List>
        </S.MainMenuSection>
      ) : null}

      <S.SubmenuWrapper $isOpen={isOpenSubDrawer} $top={top}>
        <section aria-label='Submenu' id='mobile-submenu-panel'>
          {items?.megamenu?.menuItemsCollection?.items.length ? (
            <S.List>
              {items?.megamenu?.menuItemsCollection?.items.map(
                (item, index) => {
                  return (
                    <Fragment key={index}>
                      <S.SubmenuHeaderWrapper
                        key={item?.internalName}
                        data-test-id={`MobileNavSubmenuHeaderWrapper-${index}`}
                        data-testid={`MobileNavSubmenuHeaderWrapper-${index}`}
                        style={{
                          display: isOpenSubmenu[item?.internalName ?? '']
                            ? 'block'
                            : 'none',
                        }}
                      >
                        <S.SubmenuHeaderBackButton
                          aria-label={
                            getActionAriaLabel(
                              'previous_page',
                              regionLocale.code,
                            ) || 'Back to menu'
                          }
                          data-test-id='MobileNavSubmenuHeader'
                          onClick={closeSubDrawer}
                        >
                          <Typography scale='h4'>{item?.label}</Typography>
                          <S.ArrowRightIcon aria-hidden focusable={false} />
                        </S.SubmenuHeaderBackButton>
                      </S.SubmenuHeaderWrapper>
                      <li
                        data-test-id='MobileNavSubmenuItems'
                        style={{
                          display: isOpenSubmenu[item?.internalName ?? '']
                            ? 'block'
                            : 'none',
                        }}
                      >
                        <Scroller
                          menuItem={item as ContentfulMainMenuItemFragment}
                          onNavigate={() => onClose(false)}
                          variant='mobile'
                        />
                        {makeSubmenu(item)}
                      </li>
                    </Fragment>
                  );
                },
              )}
            </S.List>
          ) : null}
        </section>
      </S.SubmenuWrapper>
    </S.Wrapper>
  );
};

export const CollectionItem = ({
  idx,
  item,
  onClose,
}: {
  idx: number;
  item: ContentfulMainMenuItemFragment;
  onClose: (isOpen: boolean) => void;
}) => {
  const { locale: regionLocale } = useRegionProvider();
  const collectionHref = getUrlText(item?.url ?? '') || '/';

  return (
    <S.CollectionLink
      aria-label={
        getLinkAriaLabel(collectionHref, regionLocale.code) ||
        item.label ||
        undefined
      }
      data-test-id={`MobileNavCollectionItem-${item?.internalName}`}
      href={collectionHref}
      onClick={() => onClose(false)}
      rel={item?.openUrlInNewTab ? 'noopener noreferrer' : undefined}
      target={item?.openUrlInNewTab ? '_blank' : '_self'}
    >
      <S.CollectionItem>
        <AspectRatioImage
          alt={item.image?.title ?? ''}
          aspectRatio={{
            x: item.image?.width ?? 1,
            y: item.image?.height ?? 1,
          }}
          className='image'
          data-test-id={`CollectionsCollectionItemImage-${idx}`}
          focalPoint={{
            x: (item.image?.width ?? 0) / 2,
            y: (item.image?.height ?? 0) / 2,
          }}
          loaderProps={{ height: 175 }}
          loading='lazy'
          originalImageDimensions={{
            x: item.image?.width ?? 0,
            y: item.image?.height ?? 0,
          }}
          showFallbackPlaceholder
          sizes='(max-width: 600px) 50vw, 20vw'
          src={item.image?.url ?? ''}
          useLoader
          width='100%'
        />
        <S.CollectionTitle data-test-id={`CollectionsCollectionTitle-${idx}`}>
          {item.label}
        </S.CollectionTitle>
      </S.CollectionItem>
    </S.CollectionLink>
  );
};

const LinkItem = ({
  item,
  onClose,
}: {
  item: ContentfulMainMenuItemFragment;
  onClose: (isOpen: boolean) => void;
}) => {
  const locale = useContext(LocaleContext);
  const {
    country: { code },
    locale: regionLocale,
  } = useRegionProvider();
  const url =
    getUrlText(item?.url ?? '') === '/stores'
      ? navToFindStores(locale, code)
      : getUrlText(item?.url ?? '');

  return (
    <S.ListItem data-test-id={`MobileNavLinkItem-${item?.internalName}`}>
      <S.LinkItem
        aria-label={getLinkAriaLabel(
          getUrlText(item?.url ?? ''),
          regionLocale.code,
        )}
        as={getUrlText(item?.url ?? '') === '/stores' ? 'a' : undefined}
        href={url}
        onClick={() => onClose(false)}
        target={item?.openUrlInNewTab ? '_blank' : '_self'}
      >
        <Typography scale='h4'>{item.label}</Typography>
      </S.LinkItem>
    </S.ListItem>
  );
};

export const ItemWithChildren = ({
  item,
  onClose,
}: {
  item: ContentfulMainMenuItemFragment;
  onClose: (isOpen: boolean) => void;
}) => {
  const locale = useContext(LocaleContext);
  const {
    country: { code },
    locale: regionLocale,
  } = useRegionProvider();

  return (
    <S.ListItemContent
      data-test-id={`MobileNavInnerListItem-${item?.internalName}`}
    >
      <MobileMenuAccordion title={item.label ?? ''}>
        <S.InnerList>
          {item?.submenuItemsCollection?.items?.map(
            (link) =>
              link?.visibleInMobile && (
                <S.InnerListItem
                  key={link?.internalName}
                  data-test-id={`MobileNavInnerListItem-${link?.internalName}`}
                >
                  <S.LinkItem
                    aria-label={getLinkAriaLabel(
                      getUrlText(link?.url ?? ''),
                      regionLocale.code,
                    )}
                    as={
                      getUrlText(link?.url ?? '') === '/stores'
                        ? 'a'
                        : undefined
                    }
                    href={
                      getUrlText(link?.url ?? '') === '/stores'
                        ? navToFindStores(locale, code)
                        : getUrlText(link?.url ?? '')
                    }
                    onClick={() => onClose(false)}
                    target={link.openUrlInNewTab ? '_blank' : '_self'}
                  >
                    {link?.label}
                  </S.LinkItem>
                </S.InnerListItem>
              ),
          )}
        </S.InnerList>
      </MobileMenuAccordion>
    </S.ListItemContent>
  );
};

MobileNav.displayName = 'MobileNav';
